/**
 * User Preferences Service Layer
 * Handles all database operations for user preferences
 * Provides CRUD operations with validation and error handling
 */

import { supabase } from '@/integrations/supabase/client';
import { 
  UserPreferences, 
  UserPreferencesUpdate, 
  DEFAULT_USER_PREFERENCES,
  isValidNotificationFrequency 
} from '@/types/userPreferences';

/**
 * Fetches the current user's preferences from the database
 * If preferences don't exist, automatically creates default preferences
 * 
 * @returns Promise resolving to UserPreferences object or null on error
 */
export async function getUserPreferences(): Promise<UserPreferences | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return null;
    }
    
    const { data, error } = await supabase
      .from('user_preferences')
      .select('*')
      .eq('user_id', user.id)
      .single();
    
    if (error) {
      // PGRST116 = no rows found - create default preferences
      if (error.code === 'PGRST116') {
        return await createDefaultPreferences(user.id);
      }
      throw error;
    }
    
    return data as UserPreferences;
  } catch (error) {
    return null;
  }
}

/**
 * Creates default preferences for a new user
 * Uses DEFAULT_USER_PREFERENCES constant for initial values
 * 
 * @param userId - UUID of the user to create preferences for
 * @returns Promise resolving to created UserPreferences or null on error
 */
export async function createDefaultPreferences(userId: string): Promise<UserPreferences | null> {
  try {
    const { data, error } = await supabase
      .from('user_preferences')
      .insert({
        user_id: userId,
        ...DEFAULT_USER_PREFERENCES,
      })
      .select('*')
      .single();
    
    if (error) throw error;
    
    return data as UserPreferences;
  } catch (error) {
    return null;
  }
}

/**
 * Updates specific user preferences with validation
 * Validates notification_frequency before database update
 * 
 * @param updates - Partial object containing fields to update
 * @returns Promise resolving to updated UserPreferences or null on error
 * @throws Error if notification_frequency is invalid
 */
export async function updateUserPreferences(
  updates: UserPreferencesUpdate
): Promise<UserPreferences | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return null;
    }
    
    // Validate notification_frequency if being updated
    if (updates.notification_frequency !== undefined && updates.notification_frequency !== null) {
      if (!isValidNotificationFrequency(updates.notification_frequency)) {
        throw new Error(`Invalid notification_frequency: ${updates.notification_frequency}. Must be one of: realtime, hourly, daily, weekly`);
      }
    }
    
    const { data, error } = await supabase
      .from('user_preferences')
      .update(updates)
      .eq('user_id', user.id)
      .select('*')
      .single();
    
    if (error) throw error;
    
    return data as UserPreferences;
  } catch (error) {
    return null;
  }
}

/**
 * Toggles a boolean preference value (true <-> false)
 * Only works for boolean preference fields
 * 
 * @param key - Name of the boolean preference field to toggle
 * @returns Promise resolving to updated UserPreferences or null on error
 */
export async function togglePreference(
  key: keyof Omit<UserPreferences, 'id' | 'user_id' | 'created_at' | 'updated_at' | 'theme' | 'theme_color' | 'notification_frequency' | 'preferences'>
): Promise<UserPreferences | null> {
  try {
    const currentPrefs = await getUserPreferences();
    if (!currentPrefs) {
      return null;
    }
    
    const currentValue = currentPrefs[key];
    if (typeof currentValue !== 'boolean') {
      return null;
    }
    
    return await updateUserPreferences({ [key]: !currentValue } as UserPreferencesUpdate);
  } catch (error) {
    return null;
  }
}

/**
 * Resets all user preferences to default values
 * WARNING: This overwrites all custom preferences
 * 
 * @returns Promise resolving to reset UserPreferences or null on error
 */
export async function resetToDefaultPreferences(): Promise<UserPreferences | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      return null;
    }
    
    const { data, error } = await supabase
      .from('user_preferences')
      .update(DEFAULT_USER_PREFERENCES)
      .eq('user_id', user.id)
      .select('*')
      .single();
    
    if (error) throw error;
    
    return data as UserPreferences;
  } catch (error) {
    return null;
  }
}

/**
 * Subscribes to real-time updates for user preferences
 * Calls the callback function whenever preferences are updated
 * 
 * @param userId - UUID of the user to subscribe to
 * @param callback - Function to call with updated preferences
 * @returns Unsubscribe function to cleanup the subscription
 * 
 * @example
 * const unsubscribe = subscribeToPreferences(userId, (prefs) => {
 *   console.log('Preferences updated:', prefs);
 * });
 * // Later, cleanup:
 * unsubscribe();
 */
export function subscribeToPreferences(
  userId: string,
  callback: (preferences: UserPreferences) => void
): () => void {
  const channel = supabase
    .channel('user_preferences_changes')
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'user_preferences',
        filter: `user_id=eq.${userId}`
      },
      (payload) => {
        callback(payload.new as UserPreferences);
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}
